// Waves on the globe's sea up close. An FFT wave field (Tessendorf): a
// JONSWAP wind sea at 7 m/s with a long swell from a distant storm, in two
// cascades, 1,024 m (swell and the wind sea's peak, about 94 m) and 128 m
// (wind waves down to 1 m). Each frame the spectrum is moved on in time,
// inverse-transformed on the GPU and written as slopes and whitecap foam into
// one two-layer array texture, which the Earth shader samples
// (EARTH_OCEAN_WAVES_GLSL in shaders/earthocean.js):
//   layer 0: slope x, slope z of the 1,024 m cascade, then of the 128 m one
//   layer 1: foam cover of each cascade
// One array keeps the Earth shader at its 16 texture units.
//
// The slope variance the waves carry is a fixed share of Cox and Munk's at
// the reference wind, and the rest (capillaries and the short waves under
// 1 m) stays in the glint's own lobe. Per octave of wavelength the share is
// handed to the shader too, so the variance a pixel cannot resolve goes back
// into the lobe: from orbit the glint is exactly what it was before.
//
// The field sits on the sphere through three fixed planes of the planet's own
// frame (triplanar), at coordinates in metres measured from an anchor near
// the camera, plus that anchor's phase in the field's period, worked out here
// in double precision. The fragment shader so sees millimetres at 2 km up
// with no seams and nothing to re-anchor as the camera moves.
import * as THREE from 'three';
import { EARTH_METRES } from './earthclose.js';

const TAU = Math.PI * 2;
const GRAVITY = 9.81;
const RESOLUTION = 256;
// Metres. The field's period on each axis must be a whole number of both
// patches and of the turned swell sampling (a 7-24-25 turn of the first
// patch, 16 degrees).
export const OCEAN_WAVE_PATCHES = [1024, 128];
export const OCEAN_WAVE_PERIOD = 25600;
// Wavelength bins for the slope variance: 2,048 m halving down to 1 m.
export const OCEAN_WAVE_OCTAVES = 12;
// Share of Cox-Munk's slope variance at the reference wind that the FFT
// carries; the rest is shorter than its finest waves.
const RESOLVED_SHARE = 0.22;
const REFERENCE_WIND = 7;
// Waves start to resolve below this altitude; above it nothing runs.
const ACTIVE_ALTITUDE = 150000;
const CHOPPINESS = 0.9;
const FOAM_RECOVERY = 0.35;

const SEA_STATE = {
  depth: 4000,
  local: { windSpeed: REFERENCE_WIND, fetchMeters: 300000, directionDegrees: 20, peakEnhancement: 3.3, scale: 1, swell: 0.4, directionality: 1, shortWaveFade: 0.01 },
  swell: { windSpeed: 12, fetchMeters: 1500000, directionDegrees: 75, peakEnhancement: 4, scale: 0.35, swell: 1, directionality: 1, shortWaveFade: 0.5 },
};

export const oceanWaveUniforms = {
  uWavesOn: { value: 0 },
  uWaveAnchor: { value: new THREE.Vector3() },
  uWaveScale: { value: 1 },
  uWavePhase: { value: new THREE.Vector3() },
  uWaveObjToWorld: { value: new THREE.Matrix3() },
  uWaveField: { value: null },
  uWaveOctaveShare: { value: new Array(OCEAN_WAVE_OCTAVES).fill(0) },
  uWaveResolvedShare: { value: RESOLVED_SHARE },
  // Height of the camera over the sea level, for the air below it (the
  // globe's haze veil); far above the air it reads as the full column.
  uCameraAltitudeM: { value: 1e9 },
};

// ---------------------------------------------------------------------------
// The spectrum (JONSWAP with TMA depth, Donelan-Banner spreading).

function mulberry32(seed) {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function gaussianPair(random) {
  const radius = Math.sqrt(-2 * Math.log(Math.max(random(), 1e-7)));
  const angle = TAU * random();
  return [radius * Math.cos(angle), radius * Math.sin(angle)];
}

function dispersion(k, depth) {
  return Math.sqrt(GRAVITY * k * Math.tanh(Math.min(k * depth, 20)));
}

function dispersionDerivative(k, depth) {
  const kh = Math.min(k * depth, 20);
  const cosh = Math.cosh(kh);
  return GRAVITY * (Math.tanh(kh) + (depth * k) / (cosh * cosh)) / Math.max(2 * dispersion(k, depth), 1e-6);
}

function tmaCorrection(omega, depth) {
  const value = omega * Math.sqrt(depth / GRAVITY);
  if (value <= 1) return 0.5 * value * value;
  if (value < 2) return 1 - 0.5 * (2 - value) * (2 - value);
  return 1;
}

function seaParameters(input) {
  return {
    ...input,
    angle: THREE.MathUtils.degToRad(input.directionDegrees),
    alpha: 0.076 * Math.pow((GRAVITY * input.fetchMeters) / (input.windSpeed * input.windSpeed), -0.22),
    peakOmega: 22 * Math.pow((input.windSpeed * input.fetchMeters) / (GRAVITY * GRAVITY), -0.33),
  };
}

function jonswap(omega, depth, p) {
  const w = Math.max(omega, 1e-4);
  const sigma = w <= p.peakOmega ? 0.07 : 0.09;
  const r = (w - p.peakOmega) / Math.max(sigma * p.peakOmega * Math.SQRT2, 1e-5);
  return p.scale * tmaCorrection(w, depth) * p.alpha * GRAVITY * GRAVITY * Math.pow(w, -5)
    * Math.exp(-1.25 * Math.pow(p.peakOmega / w, 4)) * Math.pow(p.peakEnhancement, Math.exp(-r * r));
}

function spreadNormalisation(s) {
  const s2 = s * s, s3 = s2 * s, s4 = s3 * s;
  return s < 5
    ? -0.000564 * s4 + 0.00776 * s3 - 0.044 * s2 + 0.192 * s + 0.163
    : -4.8e-8 * s4 + 1.07e-5 * s3 - 9.53e-4 * s2 + 5.9e-2 * s + 0.393;
}

function directionalSpread(theta, omega, p) {
  const ratio = Math.max(omega / p.peakOmega, 1e-4);
  // A floor keeps the short waves crested across the wind: without it they
  // spread to every direction and the sea reads as crumpled foil.
  const power = Math.max(ratio <= 1 ? 6.97 * Math.pow(ratio, 5) : 9.77 * Math.pow(ratio, -2.5), 3)
    + 16 * Math.tanh(Math.min(ratio, 20)) * p.swell * p.swell;
  const broad = (2 / Math.PI) * Math.cos(theta) ** 2;
  const directed = spreadNormalisation(power) * Math.pow(Math.abs(Math.cos((theta - p.angle) * 0.5)), 2 * power);
  return broad + (directed - broad) * p.directionality;
}

function directionalEnergy(k, theta, depth, p) {
  const omega = dispersion(k, depth);
  return jonswap(omega, depth, p) * directionalSpread(theta, omega, p) * Math.exp(-p.shortWaveFade * p.shortWaveFade * k * k);
}

// h0(k) for one cascade in the centred layout, its wavenumber band
// [cutoffLow, cutoffHigh), and its slope variance per wavelength octave as
// the shader's central differences see it.
function cascadeSpectrum(patchLength, cutoffLow, cutoffHigh, seed) {
  const N = RESOLUTION;
  const random = mulberry32(seed);
  const deltaK = TAU / patchLength;
  const spacing = patchLength / N;
  const local = seaParameters(SEA_STATE.local);
  const swell = seaParameters(SEA_STATE.swell);
  const h0 = new Float32Array(N * N * 2);
  for (let y = 0; y < N; y += 1) {
    for (let x = 0; x < N; x += 1) {
      const kx = (x - N * 0.5) * deltaK;
      const kz = (y - N * 0.5) * deltaK;
      const k = Math.hypot(kx, kz);
      const [gr, gi] = gaussianPair(random);
      if (k < cutoffLow || k >= cutoffHigh) continue;
      const theta = Math.atan2(kz, kx);
      const energy = directionalEnergy(k, theta, SEA_STATE.depth, local) + directionalEnergy(k, theta, SEA_STATE.depth, swell);
      const amplitude = Math.sqrt(Math.max(energy * 2 * Math.abs(dispersionDerivative(k, SEA_STATE.depth)) * deltaK * deltaK / Math.max(k, 1e-4), 0));
      const i = (y * N + x) * 2;
      h0[i] = gr * amplitude;
      h0[i + 1] = gi * amplitude;
    }
  }
  const packed = new Float32Array(N * N * 4);
  const octaves = new Float64Array(OCEAN_WAVE_OCTAVES);
  for (let y = 0; y < N; y += 1) {
    for (let x = 0; x < N; x += 1) {
      const i = y * N + x;
      const m = ((N - y) % N) * N + ((N - x) % N);
      packed[i * 4] = h0[i * 2];
      packed[i * 4 + 1] = h0[i * 2 + 1];
      packed[i * 4 + 2] = h0[m * 2];
      packed[i * 4 + 3] = -h0[m * 2 + 1];
      // Slope variance of this wave and its mirror, through the central
      // difference over two texels (transfer sin(k spacing) / spacing).
      const kx = (x - N * 0.5) * deltaK;
      const kz = (y - N * 0.5) * deltaK;
      const k = Math.hypot(kx, kz);
      if (k <= 0) continue;
      const power = h0[i * 2] ** 2 + h0[i * 2 + 1] ** 2 + h0[m * 2] ** 2 + h0[m * 2 + 1] ** 2;
      const slope = (Math.sin(kx * spacing) ** 2 + Math.sin(kz * spacing) ** 2) / (spacing * spacing);
      const octave = Math.floor(Math.log2(2048 * k / TAU));
      if (octave >= 0 && octave < OCEAN_WAVE_OCTAVES) octaves[octave] += power * slope;
    }
  }
  return { packed, octaves };
}

// ---------------------------------------------------------------------------
// GPU passes.

const FULLSCREEN_VERTEX = /* glsl */`
  void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

function computeMaterial(fragmentShader, uniforms, defines = {}) {
  return new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    depthTest: false,
    depthWrite: false,
    uniforms,
    defines: { N: RESOLUTION, ...defines },
    vertexShader: FULLSCREEN_VERTEX,
    fragmentShader: `precision highp float;\nprecision highp int;\n${fragmentShader}`,
  });
}

function fieldTarget(type = THREE.HalfFloatType) {
  const target = new THREE.WebGLRenderTarget(RESOLUTION, RESOLUTION, {
    type, format: THREE.RGBAFormat,
    minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter,
    wrapS: THREE.RepeatWrapping, wrapT: THREE.RepeatWrapping,
    depthBuffer: false, stencilBuffer: false,
  });
  target.texture.generateMipmaps = false;
  return target;
}

// Inverse FFT of two complex fields at once (xy and zw), from the centred
// spectrum to space: bit reversal, log2 N butterfly stages per axis, then the
// (-1)^(x+y) that undoes the centring.
const FFT_FRAGMENT = /* glsl */`
  uniform sampler2D inputTexture;
  uniform int stage;
  uniform int axis;
  uniform int operation;
  out vec4 outputColor;
  vec2 cmul(vec2 a, vec2 b) { return vec2(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x); }
  int reverseBits(int value) {
    int reversed = 0;
    for (int bit = 0; bit < LOG_N; bit++) { reversed = (reversed << 1) | (value & 1); value >>= 1; }
    return reversed;
  }
  void main() {
    ivec2 cell = ivec2(gl_FragCoord.xy);
    if (operation == 0) {
      ivec2 source = cell;
      if (axis == 0) source.x = reverseBits(cell.x); else source.y = reverseBits(cell.y);
      outputColor = texelFetch(inputTexture, source, 0);
      return;
    }
    if (operation == 1) {
      int coordinate = axis == 0 ? cell.x : cell.y;
      int span = 1 << (stage + 1);
      int halfSpan = span >> 1;
      int local = coordinate & (span - 1);
      int offset = local & (halfSpan - 1);
      int indexA = coordinate - local + offset;
      ivec2 cellA = cell, cellB = cell;
      if (axis == 0) { cellA.x = indexA; cellB.x = indexA + halfSpan; } else { cellA.y = indexA; cellB.y = indexA + halfSpan; }
      vec4 a = texelFetch(inputTexture, cellA, 0);
      vec4 b = texelFetch(inputTexture, cellB, 0);
      float angle = 6.28318530718 * float(offset) / float(span);
      vec2 tw = vec2(cos(angle), sin(angle));
      float s = local >= halfSpan ? -1.0 : 1.0;
      outputColor = vec4(a.xy + s * cmul(tw, b.xy), a.zw + s * cmul(tw, b.zw));
      return;
    }
    outputColor = texelFetch(inputTexture, cell, 0) * (((cell.x + cell.y) & 1) == 0 ? 1.0 : -1.0);
  }
`;

// h(k, t) and the horizontal displacement i k/|k| h, packed as two complex
// fields: xy height, zw (Dx + i Dz).
const EVOLVE_FRAGMENT = /* glsl */`
  uniform sampler2D h0Texture;
  uniform float time;
  uniform float patchLength;
  out vec4 outputColor;
  vec2 cmul(vec2 a, vec2 b) { return vec2(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x); }
  void main() {
    ivec2 cell = ivec2(gl_FragCoord.xy);
    vec4 initial = texelFetch(h0Texture, cell, 0);
    vec2 k = (vec2(cell) - vec2(float(N) * 0.5)) * (6.28318530718 / patchLength);
    float kLength = max(length(k), 1e-4);
    float omega = sqrt(9.81 * kLength * tanh(min(kLength * DEPTH, 20.0)));
    vec2 phase = vec2(cos(omega * time), sin(omega * time));
    vec2 h = cmul(initial.xy, phase) + cmul(initial.zw, vec2(phase.x, -phase.y));
    vec2 ih = vec2(-h.y, h.x);
    vec2 dx = ih * (k.x / kLength);
    vec2 dz = ih * (k.y / kLength);
    outputColor = vec4(h, dx.x - dz.y, dx.y + dz.x);
  }
`;

// Whitecap history from the Jacobian of the horizontal displacement: it
// snaps down where the surface folds and recovers slowly (persistent foam).
const HISTORY_FRAGMENT = /* glsl */`
  uniform sampler2D spatial;
  uniform sampler2D previous;
  uniform float patchLength;
  uniform float dt;
  out vec4 outputColor;
  vec2 horiz(ivec2 c) { return texelFetch(spatial, ivec2((c.x + N) % N, (c.y + N) % N), 0).zw; }
  void main() {
    ivec2 c = ivec2(gl_FragCoord.xy);
    float inv = float(N) / (2.0 * patchLength);
    vec2 dxl = horiz(c - ivec2(1, 0)), dxr = horiz(c + ivec2(1, 0));
    vec2 dzd = horiz(c - ivec2(0, 1)), dzu = horiz(c + ivec2(0, 1));
    float jxx = 1.0 + CHOP * (dxr.x - dxl.x) * inv;
    float jzz = 1.0 + CHOP * (dzu.y - dzd.y) * inv;
    float jxz = CHOP * 0.5 * ((dzu.x - dzd.x) + (dxr.y - dxl.y)) * inv;
    float jacobian = jxx * jzz - jxz * jxz;
    float before = texelFetch(previous, c, 0).r;
    float history = min(jacobian, before + dt * RECOVERY / max(jacobian, 0.5));
    outputColor = vec4(history, jacobian, 0.0, 1.0);
  }
`;

// Layer 0: fold-aware slopes of both cascades; layer 1: foam cover.
const SLOPES_FRAGMENT = /* glsl */`
  uniform sampler2D spatial0;
  uniform sampler2D spatial1;
  uniform sampler2D history0;
  uniform sampler2D history1;
  uniform int layer;
  out vec4 outputColor;
  vec2 slopes(sampler2D field, ivec2 c, float patchLength) {
    float inv = float(N) / (2.0 * patchLength);
    ivec2 l = ivec2((c.x - 1 + N) % N, c.y), r = ivec2((c.x + 1) % N, c.y);
    ivec2 d = ivec2(c.x, (c.y - 1 + N) % N), u = ivec2(c.x, (c.y + 1) % N);
    float sx = (texelFetch(field, r, 0).x - texelFetch(field, l, 0).x) * inv;
    float sz = (texelFetch(field, u, 0).x - texelFetch(field, d, 0).x) * inv;
    float jx = 1.0 + CHOP * (texelFetch(field, r, 0).z - texelFetch(field, l, 0).z) * inv;
    float jz = 1.0 + CHOP * (texelFetch(field, u, 0).w - texelFetch(field, d, 0).w) * inv;
    return vec2(sx / max(jx, 0.3), sz / max(jz, 0.3));
  }
  float foam(sampler2D history, ivec2 c) {
    return smoothstep(0.0, 1.0, clamp((FOAM_THRESHOLD - texelFetch(history, c, 0).r) * FOAM_SCALE, 0.0, 1.0));
  }
  void main() {
    ivec2 c = ivec2(gl_FragCoord.xy);
    if (layer == 0) outputColor = vec4(slopes(spatial0, c, PATCH0), slopes(spatial1, c, PATCH1));
    else outputColor = vec4(foam(history0, c), foam(history1, c), 0.0, 1.0);
  }
`;

class WaveEngine {
  constructor(renderer) {
    this.renderer = renderer;
    this.scene = new THREE.Scene();
    this.camera = new THREE.Camera();
    this.quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2));
    this.quad.frustumCulled = false;
    this.scene.add(this.quad);
    const logN = Math.log2(RESOLUTION);
    this.fftMaterial = computeMaterial(FFT_FRAGMENT, {
      inputTexture: { value: null }, stage: { value: 0 }, axis: { value: 0 }, operation: { value: 0 },
    }, { LOG_N: logN });
    this.ping = fieldTarget();
    this.pong = fieldTarget();

    const [L0, L1] = OCEAN_WAVE_PATCHES;
    const handoff = (TAU / L1) * 6;
    const specs = [cascadeSpectrum(L0, TAU / L0 * 0.5, handoff, 1013), cascadeSpectrum(L1, handoff, 1e9, 2026)];
    // Scale the sea so its slope variance is RESOLVED_SHARE of Cox-Munk's
    // at the reference wind (both directions, as the glint's lobe counts it).
    const raw = specs.reduce((sum, s) => sum + s.octaves.reduce((a, b) => a + b, 0), 0);
    const target = RESOLVED_SHARE * (0.003 + 0.00512 * REFERENCE_WIND);
    const gain = Math.sqrt(target / Math.max(raw, 1e-12));
    const octaves = new Array(OCEAN_WAVE_OCTAVES).fill(0);
    specs.forEach((s) => s.octaves.forEach((v, j) => { octaves[j] += v / raw; }));
    oceanWaveUniforms.uWaveOctaveShare.value = octaves;
    this.diagnostics = { rawSlopeVariance: raw, gain, octaveShare: octaves.map((v) => +v.toFixed(4)) };

    this.cascades = specs.map((spec, index) => {
      for (let i = 0; i < spec.packed.length; i += 1) spec.packed[i] *= gain;
      const h0 = new THREE.DataTexture(spec.packed, RESOLUTION, RESOLUTION, THREE.RGBAFormat, THREE.FloatType);
      h0.minFilter = THREE.NearestFilter;
      h0.magFilter = THREE.NearestFilter;
      h0.needsUpdate = true;
      const evolve = computeMaterial(EVOLVE_FRAGMENT, {
        h0Texture: { value: h0 }, time: { value: 0 }, patchLength: { value: OCEAN_WAVE_PATCHES[index] },
      }, { DEPTH: SEA_STATE.depth.toFixed(1) });
      const history = [fieldTarget(), fieldTarget()];
      const historyMaterial = computeMaterial(HISTORY_FRAGMENT, {
        spatial: { value: null }, previous: { value: null }, patchLength: { value: OCEAN_WAVE_PATCHES[index] }, dt: { value: 0 },
      }, { CHOP: CHOPPINESS.toFixed(3), RECOVERY: FOAM_RECOVERY.toFixed(3) });
      return { h0, evolve, spectrum: fieldTarget(), spatial: fieldTarget(), history, historyIndex: 0, historyMaterial };
    });
    for (const c of this.cascades) for (const t of c.history) this.clear(t, 1);

    this.field = new THREE.WebGLArrayRenderTarget(RESOLUTION, RESOLUTION, 2, { depthBuffer: false, stencilBuffer: false });
    // three r160's array target makes a bare DataArrayTexture and drops the
    // options (it would be 8-bit, clamped and nearest), so they are set on
    // the texture itself. It mipmaps render targets as TEXTURE_2D, so the
    // array's chain is generated here after each update instead.
    const fieldTexture = this.field.texture;
    fieldTexture.type = THREE.HalfFloatType;
    fieldTexture.format = THREE.RGBAFormat;
    fieldTexture.minFilter = THREE.LinearMipmapLinearFilter;
    fieldTexture.magFilter = THREE.LinearFilter;
    fieldTexture.wrapS = THREE.RepeatWrapping;
    fieldTexture.wrapT = THREE.RepeatWrapping;
    fieldTexture.anisotropy = 4;
    fieldTexture.generateMipmaps = false;
    this.slopesMaterial = computeMaterial(SLOPES_FRAGMENT, {
      spatial0: { value: null }, spatial1: { value: null }, history0: { value: null }, history1: { value: null }, layer: { value: 0 },
    }, { CHOP: CHOPPINESS.toFixed(3), PATCH0: L0.toFixed(1), PATCH1: L1.toFixed(1), FOAM_THRESHOLD: '0.32', FOAM_SCALE: '2.5' });
  }

  clear(target, value) {
    const color = new THREE.Color();
    this.renderer.getClearColor(color);
    const alpha = this.renderer.getClearAlpha();
    this.renderer.setClearColor(new THREE.Color(value, value, value), 1);
    this.renderer.setRenderTarget(target);
    this.renderer.clear(true, false, false);
    this.renderer.setClearColor(color, alpha);
  }

  pass(material, target, layer = 0) {
    this.quad.material = material;
    this.renderer.setRenderTarget(target, layer);
    this.renderer.render(this.scene, this.camera);
  }

  transform(source, output) {
    const u = this.fftMaterial.uniforms;
    const logN = Math.log2(RESOLUTION);
    let src = source.texture;
    let dst = this.ping;
    for (let axis = 0; axis < 2; axis += 1) {
      u.operation.value = 0; u.axis.value = axis; u.inputTexture.value = src;
      this.pass(this.fftMaterial, dst);
      src = dst.texture; dst = dst === this.ping ? this.pong : this.ping;
      for (let stage = 0; stage < logN; stage += 1) {
        u.operation.value = 1; u.axis.value = axis; u.stage.value = stage; u.inputTexture.value = src;
        this.pass(this.fftMaterial, dst);
        src = dst.texture; dst = dst === this.ping ? this.pong : this.ping;
      }
    }
    u.operation.value = 2; u.inputTexture.value = src;
    this.pass(this.fftMaterial, output);
  }

  update(time, dt) {
    const renderer = this.renderer;
    const previousTarget = renderer.getRenderTarget();
    const previousXr = renderer.xr.enabled;
    renderer.xr.enabled = false;
    for (const c of this.cascades) {
      c.evolve.uniforms.time.value = time;
      this.pass(c.evolve, c.spectrum);
      this.transform(c.spectrum, c.spatial);
      const prev = c.history[c.historyIndex];
      const next = c.history[1 - c.historyIndex];
      c.historyMaterial.uniforms.spatial.value = c.spatial.texture;
      c.historyMaterial.uniforms.previous.value = prev.texture;
      c.historyMaterial.uniforms.dt.value = dt;
      this.pass(c.historyMaterial, next);
      c.historyIndex = 1 - c.historyIndex;
    }
    const su = this.slopesMaterial.uniforms;
    su.spatial0.value = this.cascades[0].spatial.texture;
    su.spatial1.value = this.cascades[1].spatial.texture;
    su.history0.value = this.cascades[0].history[this.cascades[0].historyIndex].texture;
    su.history1.value = this.cascades[1].history[this.cascades[1].historyIndex].texture;
    for (let layer = 0; layer < 2; layer += 1) {
      su.layer.value = layer;
      this.pass(this.slopesMaterial, this.field, layer);
    }
    renderer.setRenderTarget(previousTarget);
    renderer.xr.enabled = previousXr;
    const gl = renderer.getContext();
    const handle = renderer.properties.get(this.field.texture).__webglTexture;
    if (handle) {
      renderer.state.bindTexture(gl.TEXTURE_2D_ARRAY, handle);
      gl.generateMipmap(gl.TEXTURE_2D_ARRAY);
      renderer.state.unbindTexture();
    }
  }

  // The FFT against analytic inputs: a centred impulse must come out as a
  // constant 1 and a one-bin x impulse as exp(2 pi i x / N). Float targets.
  validate() {
    const renderer = this.renderer;
    if (!renderer.extensions.has('EXT_color_buffer_float')) return { pass: true, skipped: true };
    const N = RESOLUTION;
    const out = fieldTarget(THREE.FloatType);
    const saved = [this.ping, this.pong];
    this.ping = fieldTarget(THREE.FloatType);
    this.pong = fieldTarget(THREE.FloatType);
    const pixels = new Float32Array(N * N * 4);
    const run = (fx) => {
      const data = new Float32Array(N * N * 4);
      data[((N / 2) * N + N / 2 + fx) * 4] = 1;
      data[((N / 2) * N + N / 2 + fx) * 4 + 2] = 1;
      const tex = new THREE.DataTexture(data, N, N, THREE.RGBAFormat, THREE.FloatType);
      tex.needsUpdate = true;
      const previousTarget = renderer.getRenderTarget();
      this.transform({ texture: tex }, out);
      renderer.readRenderTargetPixels(out, 0, 0, N, N, pixels);
      renderer.setRenderTarget(previousTarget);
      tex.dispose();
      let error = 0;
      for (let y = 0; y < N; y += 1) {
        for (let x = 0; x < N; x += 1) {
          const o = (y * N + x) * 4;
          const a = TAU * fx * x / N;
          error = Math.max(error, Math.abs(pixels[o] - Math.cos(a)), Math.abs(pixels[o + 1] - Math.sin(a)),
            Math.abs(pixels[o + 2] - Math.cos(a)), Math.abs(pixels[o + 3] - Math.sin(a)));
        }
      }
      return error;
    };
    const impulseError = run(0);
    const frequencyError = run(1);
    this.ping.dispose(); this.pong.dispose(); out.dispose();
    [this.ping, this.pong] = saved;
    return { pass: impulseError < 1e-3 && frequencyError < 1e-3, impulseError, frequencyError };
  }
}

let engine = null;
let engineFailed = false;
let lastTime = 0;
const _cam = new THREE.Vector3();
const _inverse = new THREE.Matrix4();
const _rotation = new THREE.Matrix4();

// Per frame, from the viewer: the camera's height for the haze, and while it
// is low enough, the waves and the anchor of their coordinates.
// active: the viewer owns the Earth (not rocketry, To Scale or another
// system); wavesAllowed: the graphics preset runs the waves (not on low).
export function updateOceanWaves(renderer, earthMesh, camera, nowMs, active, wavesAllowed = true) {
  if (!earthMesh || !earthMesh.geometry?.parameters?.radius) return;
  const radius = earthMesh.geometry.parameters.radius;
  const metresPerUnit = EARTH_METRES / radius;
  earthMesh.updateWorldMatrix(true, false);
  camera.updateMatrixWorld();
  _cam.setFromMatrixPosition(camera.matrixWorld).applyMatrix4(_inverse.copy(earthMesh.matrixWorld).invert());
  const altitude = (_cam.length() - radius) * metresPerUnit;
  oceanWaveUniforms.uCameraAltitudeM.value = active ? altitude : 1e9;
  if (!active || !wavesAllowed || altitude > ACTIVE_ALTITUDE || engineFailed) {
    oceanWaveUniforms.uWavesOn.value = 0;
    return;
  }
  if (!engine) {
    try {
      engine = new WaveEngine(renderer);
      const check = engine.validate();
      engine.diagnostics.fft = check;
      if (!check.pass) {
        console.warn('[ocean waves] FFT check failed, waves off', check);
        engineFailed = true;
        oceanWaveUniforms.uWavesOn.value = 0;
        return;
      }
      oceanWaveUniforms.uWaveField.value = engine.field.texture;
      if (typeof window !== 'undefined') window.__oceanWaves = engine;
    } catch (error) {
      console.warn('[ocean waves] disabled', error);
      engineFailed = true;
      return;
    }
  }
  // The anchor: the camera's ground point, as the float32 the shader holds,
  // and its place in the field's period worked out in double.
  const anchor = oceanWaveUniforms.uWaveAnchor.value;
  anchor.copy(_cam).setLength(radius);
  anchor.set(Math.fround(anchor.x), Math.fround(anchor.y), Math.fround(anchor.z));
  const T = OCEAN_WAVE_PERIOD;
  const phase = (v) => ((v * metresPerUnit) % T + T) % T;
  oceanWaveUniforms.uWavePhase.value.set(phase(anchor.x), phase(anchor.y), phase(anchor.z));
  oceanWaveUniforms.uWaveScale.value = metresPerUnit;
  _rotation.extractRotation(earthMesh.matrixWorld);
  oceanWaveUniforms.uWaveObjToWorld.value.setFromMatrix4(_rotation);

  // The sea moves slowly at these heights: 40 updates a second are as
  // smooth as every frame and halve the FFT's cost at 60 fps.
  const now = nowMs / 1000;
  oceanWaveUniforms.uWavesOn.value = 1;
  if (lastTime && now - lastTime < 1 / 40) return;
  const dt = lastTime ? Math.min(Math.max(now - lastTime, 0), 0.1) : 1 / 60;
  lastTime = now;
  engine.update(now, dt);
}
